import { cn, formatPrice } from "@/shared/lib/utils";
import * as React from "react";
import TransparentButton from "../buttons/TransparentButton";
import Typography from "../Typography";

import { NavLink } from "react-router-dom";
import type { ProjectType } from "@/entities/projects/model/types";
import { useAppSelector } from "@/app/store/hooks";
import { makeSelectDistrictById } from "@/entities/site_data/model/selects";
import MoreButton from "../buttons/MoreButton";

export interface IProjectCardAltProps {
    project: ProjectType;
    className?: string;
    isActive?: boolean;
}

const ProjectCardAlt = React.forwardRef<HTMLDivElement, IProjectCardAltProps>(
    ({ className, project, isActive }, ref) => {
        const selectDistrict = makeSelectDistrictById(
            project.location.districtId
        );
        const district = useAppSelector(selectDistrict);

        const building = `${new Date(project.startDate).getFullYear()} - ${new Date(project.completionDate).getFullYear()}`;

        const imgRef = React.useRef<HTMLImageElement>(null);
        const cardRef = React.useRef<HTMLDivElement>(null);
        const [isHovered, setIsHovered] = React.useState(false);

        React.useEffect(() => {
            const imgEl = imgRef.current;
            const cardEl = cardRef.current;
            if (!imgEl || !cardEl) return;

            const isMobile = window.matchMedia(
                "(hover: none) and (pointer: coarse)"
            ).matches;

            const handleEnter = () => {
                setIsHovered(true);
            };

            const handleLeave = () => {
                setIsHovered(false);
            };

            if (isMobile) {
                cardEl.addEventListener("touchstart", handleEnter);
                cardEl.addEventListener("touchend", handleLeave);
            } else {
                cardEl.addEventListener("mouseenter", handleEnter);
                cardEl.addEventListener("mouseleave", handleLeave);
            }

            return () => {
                if (isMobile) {
                    cardEl.removeEventListener("touchstart", handleEnter);
                    cardEl.removeEventListener("touchend", handleLeave);
                } else {
                    cardEl.removeEventListener("mouseenter", handleEnter);
                    cardEl.removeEventListener("mouseleave", handleLeave);
                }
            };
        }, [ref]);
        
        const shouldScale = isActive || isHovered;
        const scaleValue = shouldScale ? 1.15 : 1;

        return (
            <div
                ref={cardRef}
                className={cn(
                    "relative min-w-[300px] h-[300px] md:min-w-[400px] md:h-[400px] overflow-hidden",
                    className
                )}
            >
                <img
                    ref={imgRef}
                    className="absolute z-0 w-full h-full object-cover object-center transition-transform duration-300 ease-in-out"
                    style={{
                        transform: `scale(${scaleValue})`
                    }}
                    src={
                        project.imageUrl
                            ? project.imageUrl
                            : "/images/backgrounds/card.png"
                    }
                    alt=""
                />

                <div
                    className="relative w-full h-full p-[10px] md:p-[20px] flex flex-col justify-between"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)",
                    }}
                >
                    <div className="flex flex-row gap-[8px]">
                        <TransparentButton className="bg-white-50 font-[400] text-[10px] md:text-[14px] p-[10px] cursor-default">
                            {project.location.metro}
                        </TransparentButton>
                        <TransparentButton className="bg-white-50 font-[400] text-[10px] md:text-[14px] p-[10px] cursor-default">
                            {district?.name}
                        </TransparentButton>
                    </div>

                    <div className="flex flex-col gap-[16px] md:gap-[20px]">
                        <div className="flex flex-col gap-[12px] xl:gap-[16px]">
                            <Typography className="uppercase text-white text-[18px] md:text-[20px] font-semibold md:font-bold">
                                Новостройки {building}
                            </Typography>
                            <div className="flex flex-row justify-between">
                                <Typography className="text-white uppercase text-[14px] md:text-[16px] font-semibold">
                                    ОТ {formatPrice(project.priceFrom)}
                                </Typography>
                                <Typography className="text-white uppercase text-[14px] md:text-[16px] font-semibold">
                                    ОТ {project.spec.areaFrom} кв м
                                </Typography>
                            </div>
                        </div>

                        <NavLink to={`/project/${project.id}`}>
                            <MoreButton className="">
                                Подробнее
                            </MoreButton>
                        </NavLink>
                    </div>
                </div>
            </div>
        );
    }
);

ProjectCardAlt.displayName = "ProjectCardAlt";

export default ProjectCardAlt;